import { env } from '@/lib/env';

/**
 * POST /api/public/intermediary-onboarding/request — anonymous registration.
 *
 * The BE is intentionally non-enumerable: any duplicate of CF / VAT / email
 * surfaces as the same generic 200 a happy submission produces, so the FE
 * has nothing to discriminate on. We only branch on:
 *   200/201 → success (always treat the same)
 *   400     → field validation error from Zod (show details on the form)
 *   403     → Turnstile token failed verification
 *   429     → rate limit exceeded (5 req/min/IP)
 *   else    → opaque failure
 *
 * Duplicate detection is NOT exposed to the FE by design — the BE writes a
 * BLOCKED audit event without telling the caller.
 */

const ENDPOINT = '/api/public/intermediary-onboarding/request';

export type OrganizationAddressType =
  | 'REGISTERED_OFFICE'
  | 'OPERATIVE_OFFICE'
  | 'RESIDENCE'
  | 'DOMICILE'
  | 'MAILING';

export interface AddressPayload {
  addressType: OrganizationAddressType;
  street: string;
  city: string;
  province: string;
  zip: string;
  country: string;
  isPrimary?: boolean;
}

export type OrganizationContactChannelType =
  | 'MOBILE'
  | 'PHONE'
  | 'EMAIL'
  | 'PEC';

export interface ContactChannelPayload {
  type: OrganizationContactChannelType;
  value: string;
  isPrimary?: boolean;
}

export type WithholdingType =
  | 'ORDINARIA_23_50'
  | 'RIDOTTA_23_20'
  | 'FORFETTARIO_0'
  | 'PRESTAZIONE_OCCASIONALE_20';

export interface OrganizationPayload {
  legalNature: 'PERSON' | 'COMPANY';
  legalName: string;
  orgType: 'BROKER' | 'AGENZIA' | 'INTERMEDIARIO';
  fiscalCode?: string;
  vatNumber?: string;
  ruiSection?: 'A' | 'B' | 'C' | 'D' | 'E' | 'F';
  ruiNumber?: string;
  ruiRegistrationDate?: string;
  /** Codice SDI — fatturazione elettronica. COMPANY only. Optional. */
  sdiCode?: string;
  /** Italian forma giuridica (closed list on FE; BE accepts free text). */
  businessLegalForm?: string;
  withholdingType?: WithholdingType;
  /**
   * Legacy single-address mirror — still required at the BE wire (PR-A
   * did not drop it). Derived from `addresses[0]` on the FE submit
   * mapper so the payload stays compatible with existing read paths.
   */
  headquartersAddress: {
    street: string;
    city: string;
    province: string;
    zip: string;
    country: string;
  };
  /** Multi-address (PR-A optional, PR-C populates). Max 8 per BE schema. */
  addresses?: AddressPayload[];
  /** Multi-channel contacts (PR-C). Max 20 per BE schema. */
  contactChannels?: ContactChannelPayload[];
}

export interface AdminUserPayload {
  fullName: string;
  email: string;
  fiscalCode: string;
  // PR-A.1: identity fields are now REQUIRED end-to-end on the BE.
  sex: 'M' | 'F';
  dateOfBirth: string;
  birthCountry: string;
  // Required iff `birthCountry === 'IT'` (BE cross-field rule).
  birthMunicipalityCadastralCode?: string;
  placeOfBirth?: string;
  // Personal RUI of the responsible — required for AGENZIA/BROKER COMPANY
  // (PR-ONBOARDING-COMPANY-RESPONSIBLE-RUI-1). Section derived by the BE.
  ruiNumber?: string;
  ruiRegistrationDate?: string;
}

export interface SubmitOnboardingPayload {
  organization: OrganizationPayload;
  adminUser: AdminUserPayload;
  agreement: {
    agreementType: 'B_WITH_A' | 'B_WITH_B' | 'B_WITH_E';
  };
  turnstileToken: string;
  /**
   * Stable client-generated UUID for log correlation across the entire
   * wizard tab. Visible in the URL as `?sid=<uuid>`. Optional on the
   * BE — when present every log line / outbox audit row carries it
   * so ops can grep all activity for a single failed wizard attempt.
   */
  wizardSessionId?: string;
}

export type SubmitErrorCode =
  | 'VALIDATION_FAILED'
  | 'TURNSTILE_INVALID'
  | 'RATE_LIMITED'
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

export interface FieldIssue {
  path: ReadonlyArray<string | number>;
  message: string;
}

export type SubmitResult =
  | { ok: true; submissionId: string; uploadToken?: string }
  | {
      ok: false;
      code: SubmitErrorCode;
      submissionId?: string;
      fieldIssues?: FieldIssue[];
    };

interface SubmitSuccessBody {
  data?: {
    status?: string;
    submissionId?: string;
    uploadToken?: string;
  };
}

interface ValidationFailedBody {
  error: string;
  message?: string;
  /** Per-submission UUID surfaced on every failure (PR-FIX-MOBILE-AND-ID). */
  submissionId?: string;
  details?: ReadonlyArray<{ path?: ReadonlyArray<string | number>; message?: string }>;
}

/**
 * Map raw HTTP outcomes to the FE error vocabulary.
 *
 * The BE never sends 409 / 5xx-with-body for domain conflicts on this
 * endpoint (anti-enumeration), so the only 4xx that carries actionable
 * detail is 400 (Zod validation). Everything else collapses to a generic
 * "verifica sicurezza" or "riprova più tardi" message in the UI.
 */
export async function submitOnboardingRequest(
  payload: SubmitOnboardingPayload,
): Promise<SubmitResult> {
  let response: Response;
  try {
    response = await fetch(`${env.VITE_API_BASE_URL}${ENDPOINT}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    return { ok: false, code: 'NETWORK_ERROR' };
  }

  if (response.ok) {
    // Anti-enumeration: the BE returns the same 200 envelope on success and
    // BLOCKED. The discriminator is the optional `uploadToken` field — present
    // only when the request actually created the censimento. We forward it
    // verbatim and let the caller decide whether to advance to the upload
    // step or redirect straight to the success page.
    const body = await safeJson<SubmitSuccessBody>(response);
    const submissionId =
      typeof body?.data?.submissionId === 'string'
        ? body.data.submissionId
        : '';
    const token = typeof body?.data?.uploadToken === 'string'
      ? body.data.uploadToken
      : undefined;
    return token !== undefined
      ? { ok: true, submissionId, uploadToken: token }
      : { ok: true, submissionId };
  }

  // Single read of the error body — `response.json()` can only be
  // called once. Branch on status afterwards to map to the FE error
  // vocabulary; submissionId surfaces uniformly.
  const errBody = await safeJson<ValidationFailedBody>(response);
  const submissionId = errBody?.submissionId;

  if (response.status === 400) {
    const fieldIssues: FieldIssue[] = (errBody?.details ?? [])
      .filter((d): d is { path: ReadonlyArray<string | number>; message: string } =>
        Array.isArray(d.path) && typeof d.message === 'string',
      )
      .map((d) => ({ path: d.path, message: d.message }));
    return { ok: false, code: 'VALIDATION_FAILED', submissionId, fieldIssues };
  }

  if (response.status === 403) {
    return { ok: false, code: 'TURNSTILE_INVALID', submissionId };
  }
  if (response.status === 429) {
    return { ok: false, code: 'RATE_LIMITED', submissionId };
  }
  return { ok: false, code: 'UNKNOWN', submissionId };
}

async function safeJson<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

// ─── Document upload (PR-BE-2 / PR-FE-2) ────────────────────────────────────
//
// Three-call sequence per file:
//   1. signOnboardingDocumentUpload  → returns `signedUrl` + `documentId`
//   2. uploadFileToSignedUrl         → PUT bytes directly to GCS
//   3. confirmOnboardingDocumentUpload → BE marks the row UPLOADED
//
// Authorisation: bearer `uploadToken` returned by submitOnboardingRequest.
// Token is ALWAYS in memory only — never URL, never localStorage.
//
// Error vocabulary mirrors the BE:
//   400 → VALIDATION_FAILED  (mime/size/type rejected by Zod)
//   403 → UPLOAD_TOKEN_INVALID
//   404 → DOCUMENT_NOT_FOUND
//   409 → UPLOAD_NOT_RECEIVED (object missing on confirm)
//   429 → RATE_LIMITED
//   else → UNKNOWN

export type UploadDocumentType =
  | 'DOCUMENTO_IDENTITA'
  | 'CODICE_FISCALE'
  | 'VISURA_CAMERALE'
  | 'RC_PROFESSIONALE';

export type UploadMimeType =
  | 'application/pdf'
  | 'image/jpeg'
  | 'image/png';

export interface SignDocumentUploadInput {
  uploadToken: string;
  documentType: UploadDocumentType;
  originalFilename: string;
  mimeType: UploadMimeType;
  sizeBytes: number;
  /** Wizard tab session id — see `SubmitOnboardingPayload.wizardSessionId`. */
  wizardSessionId?: string;
}

export interface SignDocumentUploadResult {
  documentId: string;
  storageKey: string;
  signedUrl: string;
  expiresAt: string;
}

export type UploadErrorCode =
  | 'VALIDATION_FAILED'
  | 'UPLOAD_TOKEN_INVALID'
  | 'DOCUMENT_NOT_FOUND'
  | 'UPLOAD_NOT_RECEIVED'
  | 'RATE_LIMITED'
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

export type SignUploadResult =
  | { ok: true; data: SignDocumentUploadResult }
  | { ok: false; code: UploadErrorCode; fieldIssues?: FieldIssue[] };

export type ConfirmUploadResult =
  | { ok: true }
  | { ok: false; code: UploadErrorCode };

export type PutToSignedUrlResult =
  | { ok: true }
  | { ok: false; code: 'NETWORK_ERROR' | 'UNKNOWN'; status?: number };

const SIGN_UPLOAD_ENDPOINT =
  '/api/public/intermediary-onboarding/documents/sign-upload';

export async function signOnboardingDocumentUpload(
  input: SignDocumentUploadInput,
): Promise<SignUploadResult> {
  let response: Response;
  try {
    response = await fetch(`${env.VITE_API_BASE_URL}${SIGN_UPLOAD_ENDPOINT}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  } catch {
    return { ok: false, code: 'NETWORK_ERROR' };
  }

  if (response.ok) {
    const body = await safeJson<{ data?: SignDocumentUploadResult }>(response);
    if (!body?.data) return { ok: false, code: 'UNKNOWN' };
    return { ok: true, data: body.data };
  }
  const mapped = await mapUploadHttpError(response);
  return { ok: false, ...mapped };
}

const CONFIRM_UPLOAD_ENDPOINT_PATH =
  '/api/public/intermediary-onboarding/documents';

export async function confirmOnboardingDocumentUpload(
  documentId: string,
  uploadToken: string,
  wizardSessionId?: string,
): Promise<ConfirmUploadResult> {
  let response: Response;
  try {
    response = await fetch(
      `${env.VITE_API_BASE_URL}${CONFIRM_UPLOAD_ENDPOINT_PATH}/${encodeURIComponent(documentId)}/confirm-upload`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uploadToken, wizardSessionId }),
      },
    );
  } catch {
    return { ok: false, code: 'NETWORK_ERROR' };
  }

  if (response.ok) return { ok: true };
  const mapped = await mapUploadHttpError(response);
  return { ok: false, code: mapped.code };
}

/**
 * PUT the file bytes to the GCS signed URL. The Content-Type header MUST
 * match the value the URL was signed for (otherwise GCS returns 403).
 */
export async function uploadFileToSignedUrl(
  signedUrl: string,
  file: Blob,
  mimeType: string,
): Promise<PutToSignedUrlResult> {
  let response: Response;
  try {
    response = await fetch(signedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': mimeType },
      body: file,
    });
  } catch {
    return { ok: false, code: 'NETWORK_ERROR' };
  }
  if (response.ok) return { ok: true };
  return { ok: false, code: 'UNKNOWN', status: response.status };
}

async function mapUploadHttpError(
  response: Response,
): Promise<{ code: UploadErrorCode; fieldIssues?: FieldIssue[] }> {
  if (response.status === 400) {
    const body = await safeJson<ValidationFailedBody>(response);
    const fieldIssues: FieldIssue[] = (body?.details ?? [])
      .filter((d): d is { path: ReadonlyArray<string | number>; message: string } =>
        Array.isArray(d.path) && typeof d.message === 'string',
      )
      .map((d) => ({ path: d.path, message: d.message }));
    return { code: 'VALIDATION_FAILED', fieldIssues };
  }
  if (response.status === 403) return { code: 'UPLOAD_TOKEN_INVALID' };
  if (response.status === 404) return { code: 'DOCUMENT_NOT_FOUND' };
  if (response.status === 409) return { code: 'UPLOAD_NOT_RECEIVED' };
  if (response.status === 429) return { code: 'RATE_LIMITED' };
  return { code: 'UNKNOWN' };
}

// ─── Company lookup (PR-OpenAPI-2) ──────────────────────────────────────────
//
// GET /api/public/intermediary-onboarding/company-lookup
//
// Read-only enrichment for the public wizard. Looks up an Italian company
// by P.IVA / CF and returns a normalized DTO the FE uses to pre-compile the
// "dati azienda" step. Never blocks onboarding:
//   - 200 OK     → either FOUND (company data inside) or NOT_FOUND
//   - 503        → provider unavailable / timeout / rate-limited / env not
//                    configured. The FE shows a soft message; manual flow
//                    continues unaffected.
// Anti-abuse: same Turnstile + publicLimiter stack as the main /request
// endpoint, plus a 24h memory cache server-side (repeated lookups for the
// same code → 0 provider call).

const COMPANY_LOOKUP_ENDPOINT =
  '/api/public/intermediary-onboarding/company-lookup';

export type CompanyLookupActivityStatus = 'ACTIVE' | 'CEASED' | 'UNKNOWN';

export interface CompanyLookupAddress {
  street?: string;
  streetNumber?: string;
  city?: string;
  province?: string;
  zip?: string;
  country?: string;
  cadastralCode?: string;
}

export interface CompanyLookupCompany {
  legalName?: string;
  vatNumber?: string;
  fiscalCode?: string;
  pecEmail?: string;
  sdiCode?: string;
  providerLegalFormCode?: string;
  providerLegalFormLabel?: string;
  /** Mappato dal BE alla whitelist `BUSINESS_LEGAL_FORMS` quando possibile. */
  businessLegalForm?: string;
  activityStatus?: CompanyLookupActivityStatus;
  legalAddress?: CompanyLookupAddress;
}

export type CompanyLookupNotFoundReason =
  | 'NOT_FOUND'
  | 'PROVIDER_NOT_CONFIGURED'
  | 'PROVIDER_TIMEOUT'
  | 'PROVIDER_RATE_LIMIT'
  | 'PROVIDER_UNAVAILABLE';

export type CompanyLookupErrorCode =
  | 'VALIDATION_FAILED'
  | 'TURNSTILE_INVALID'
  | 'RATE_LIMITED'
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

/**
 * Discriminated union — the FE pattern-matches on `kind` to render a
 * loading / found / not-found / unavailable / error UI. Provider-side
 * problems (`unavailable`) are SOFT: the user can keep filling the form
 * manually.
 */
export type CompanyLookupResult =
  | { kind: 'found'; company: CompanyLookupCompany }
  | { kind: 'not_found' }
  | { kind: 'unavailable'; reason: CompanyLookupNotFoundReason }
  | { kind: 'error'; code: CompanyLookupErrorCode };

export async function lookupCompany(
  vatOrFiscalCode: string,
  turnstileToken: string,
  wizardSessionId?: string,
): Promise<CompanyLookupResult> {
  const url = new URL(
    `${env.VITE_API_BASE_URL}${COMPANY_LOOKUP_ENDPOINT}`,
    window.location.origin,
  );
  url.searchParams.set('vatOrFiscalCode', vatOrFiscalCode);
  url.searchParams.set('turnstileToken', turnstileToken);
  if (wizardSessionId) {
    url.searchParams.set('wizardSessionId', wizardSessionId);
  }

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
  } catch {
    return { kind: 'error', code: 'NETWORK_ERROR' };
  }

  if (response.status === 200) {
    const body = await safeJson<{
      data?: {
        found: boolean;
        source: string;
        company: CompanyLookupCompany | null;
      };
    }>(response);
    if (!body?.data) return { kind: 'error', code: 'UNKNOWN' };
    if (body.data.found && body.data.company) {
      return { kind: 'found', company: body.data.company };
    }
    return { kind: 'not_found' };
  }

  if (response.status === 503) {
    const body = await safeJson<{
      data?: {
        found: false;
        source: string;
        company: null;
        reason?: CompanyLookupNotFoundReason;
      };
    }>(response);
    return {
      kind: 'unavailable',
      reason: body?.data?.reason ?? 'PROVIDER_UNAVAILABLE',
    };
  }

  if (response.status === 400) return { kind: 'error', code: 'VALIDATION_FAILED' };
  if (response.status === 403) return { kind: 'error', code: 'TURNSTILE_INVALID' };
  if (response.status === 429) return { kind: 'error', code: 'RATE_LIMITED' };
  return { kind: 'error', code: 'UNKNOWN' };
}

// ─── Identity precheck (PR-F) ───────────────────────────────────────────────
//
// POST /api/public/intermediary-onboarding/identity-precheck
//
// Dry-run del gate di submit. Chiamato sulla "Continua" dello step
// `/amministratore` per evitare di far compilare gli step successivi a
// chi è già destinato al BLOCKED. Il submit finale resta source of
// truth: se il precheck viene bypassato (cache, devtools), il submit
// blocca comunque.
//
// Soft-fail: errori 4xx (Turnstile / validation) e 5xx tornano `error`,
// il consumer FE può comunque lasciare proseguire l'utente. Il banner
// "blocco" si attiva SOLO su `kind: 'conflict'`.

export interface IdentityPrecheckPayload {
  organization: {
    legalNature: 'PERSON' | 'COMPANY';
    fiscalCode?: string;
    vatNumber?: string;
  };
  adminUser: {
    fiscalCode: string;
    email: string;
  };
  turnstileToken: string;
  /** Wizard tab session id — see `SubmitOnboardingPayload.wizardSessionId`. */
  wizardSessionId?: string;
}

export type IdentityPrecheckErrorCode =
  | 'VALIDATION_FAILED'
  | 'TURNSTILE_INVALID'
  | 'RATE_LIMITED'
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

export type IdentityPrecheckResult =
  | { kind: 'clean'; submissionId: string }
  | { kind: 'conflict'; submissionId: string }
  | { kind: 'error'; code: IdentityPrecheckErrorCode; submissionId?: string };

const PRECHECK_ENDPOINT =
  '/api/public/intermediary-onboarding/identity-precheck';

export async function precheckIdentity(
  payload: IdentityPrecheckPayload,
): Promise<IdentityPrecheckResult> {
  let response: Response;
  try {
    response = await fetch(`${env.VITE_API_BASE_URL}${PRECHECK_ENDPOINT}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    return { kind: 'error', code: 'NETWORK_ERROR' };
  }

  if (response.status === 200) {
    const body = await safeJson<{
      data?: { status?: 'CLEAN' | 'CONFLICT'; submissionId?: string };
    }>(response);
    const submissionId = body?.data?.submissionId;
    if (body?.data?.status === 'CLEAN' && submissionId) {
      return { kind: 'clean', submissionId };
    }
    if (body?.data?.status === 'CONFLICT' && submissionId) {
      return { kind: 'conflict', submissionId };
    }
    return { kind: 'error', code: 'UNKNOWN' };
  }

  if (response.status === 400) return { kind: 'error', code: 'VALIDATION_FAILED' };
  if (response.status === 403) return { kind: 'error', code: 'TURNSTILE_INVALID' };
  if (response.status === 429) return { kind: 'error', code: 'RATE_LIMITED' };
  return { kind: 'error', code: 'UNKNOWN' };
}

// ── RUI auto-lookup (PR-ONBOARDING-RUI-AUTO-LOOKUP-1) ────────────────────────

export interface RuiLookupApiInput {
  subject: 'ORGANIZATION' | 'RESPONSIBLE';
  orgType: 'AGENZIA' | 'BROKER' | 'INTERMEDIARIO';
  legalNature?: 'PERSON' | 'COMPANY' | null;
  ruiNumber: string;
  declaredName?: string | null;
  organizationRuiNumber?: string | null;
  wizardSessionId?: string;
}

export interface RuiLookupApiResult {
  found: boolean;
  providerAvailable: boolean;
  ruiNumber: string | null;
  ruiSection: string | null;
  subjectKind: string | null;
  displayName: string | null;
  registrationDate: string | null;
  inoperative: boolean | null;
  sectionOk: boolean;
  subjectKindOk: boolean;
  nameMatch: 'VERIFIED' | 'REVIEW_REQUIRED' | 'NAME_MISMATCH' | 'NOT_CHECKED';
  linkStatus:
    | 'VERIFIED'
    | 'USER_NOT_LINKED_TO_ORG'
    | 'LINKED_TO_OTHER_INTERMEDIARY'
    | 'REVIEW_REQUIRED'
    | 'NOT_APPLICABLE';
  linkKind: string | null;
  outcome: string;
  reasons: string[];
  message: string;
}

const RUI_LOOKUP_ENDPOINT = '/api/public/intermediary-onboarding/rui-lookup';

/** Stateless RUI cache lookup. Returns null on network/non-200 (caller degrades). */
export async function lookupRui(
  input: RuiLookupApiInput,
): Promise<RuiLookupApiResult | null> {
  let response: Response;
  try {
    response = await fetch(`${env.VITE_API_BASE_URL}${RUI_LOOKUP_ENDPOINT}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  } catch {
    return null;
  }
  if (response.status !== 200) return null;
  const body = await safeJson<{ data?: RuiLookupApiResult }>(response);
  return body?.data ?? null;
}

// ─── Identity verification KYC (PR-DIDIT-6) ─────────────────────────────────
//
// Post-submit, token-gated self-service KYC. Mirrors the document-upload
// bearer model: the `uploadToken` from submitOnboardingRequest authorises the
// call and is ALWAYS in the request body — never URL / query / localStorage.
//
//   POST .../identity-verification/session         → start or RESUME a session
//   POST .../identity-verification/session/status  → poll the normalized status
//
// Normalized status (BE deriveNormalizedKycStatus, last-session authoritative).
// Error vocabulary:
//   403 → UPLOAD_TOKEN_INVALID   (session expired — caller drops the page)
//   502/503 → PROVIDER_UNAVAILABLE (Didit not configured / provider error →
//             clean, non-technical copy; provider/secret details never shown)
//   429 → RATE_LIMITED
//   else → UNKNOWN

export type KycNormalizedStatus =
  | 'NOT_STARTED'
  | 'SESSION_CREATED'
  | 'IN_PROGRESS'
  | 'VERIFIED'
  | 'REVIEW_REQUIRED'
  | 'FAILED'
  | 'EXPIRED'
  | 'CANCELLED'
  | 'PROVIDER_ERROR';

export interface KycDecisionSummary {
  decision: string;
  riskLevel: string | null;
  decidedAt: string | null;
  createdAt: string;
}

export type KycActionErrorCode =
  | 'UPLOAD_TOKEN_INVALID'
  | 'PROVIDER_UNAVAILABLE'
  | 'RATE_LIMITED'
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

export interface KycStatusData {
  status: KycNormalizedStatus;
  requiresKyc: boolean;
  latestSessionStatus: string | null;
  latestDecision: KycDecisionSummary | null;
}

export interface KycSessionData {
  status: KycNormalizedStatus;
  requiresKyc: boolean;
  /** Provider link to redirect the applicant to; null when already terminal. */
  redirectUrl: string | null;
  sessionId: string | null;
  latestDecision: KycDecisionSummary | null;
}

export type KycStatusResult =
  | { ok: true; data: KycStatusData }
  | { ok: false; code: KycActionErrorCode };

export type KycSessionResult =
  | { ok: true; data: KycSessionData }
  | { ok: false; code: KycActionErrorCode };

const KYC_SESSION_ENDPOINT =
  '/api/public/intermediary-onboarding/identity-verification/session';
const KYC_STATUS_ENDPOINT =
  '/api/public/intermediary-onboarding/identity-verification/session/status';

/** Map a non-2xx HTTP status to the FE KYC error vocabulary. */
function mapKycHttpError(status: number): KycActionErrorCode {
  if (status === 403) return 'UPLOAD_TOKEN_INVALID';
  if (status === 502 || status === 503) return 'PROVIDER_UNAVAILABLE';
  if (status === 429) return 'RATE_LIMITED';
  return 'UNKNOWN';
}

/** Read-only poll of the normalized KYC status for the staging request. */
export async function getIdentityVerificationStatus(
  uploadToken: string,
): Promise<KycStatusResult> {
  let response: Response;
  try {
    response = await fetch(`${env.VITE_API_BASE_URL}${KYC_STATUS_ENDPOINT}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uploadToken }),
    });
  } catch {
    return { ok: false, code: 'NETWORK_ERROR' };
  }
  if (response.ok) {
    const body = await safeJson<{ data?: KycStatusData }>(response);
    if (!body?.data) return { ok: false, code: 'UNKNOWN' };
    return { ok: true, data: body.data };
  }
  return { ok: false, code: mapKycHttpError(response.status) };
}

/**
 * Start OR resume the applicant's KYC session. The BE is idempotent: it
 * resumes an in-flight session (returns the existing redirectUrl) and never
 * re-runs over VERIFIED / REVIEW_REQUIRED. When `redirectUrl` is present the
 * caller navigates the user to the provider.
 */
export async function startIdentityVerificationSession(
  uploadToken: string,
  wizardSessionId?: string,
): Promise<KycSessionResult> {
  let response: Response;
  try {
    response = await fetch(`${env.VITE_API_BASE_URL}${KYC_SESSION_ENDPOINT}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uploadToken, wizardSessionId }),
    });
  } catch {
    return { ok: false, code: 'NETWORK_ERROR' };
  }
  if (response.ok) {
    const body = await safeJson<{ data?: KycSessionData }>(response);
    if (!body?.data) return { ok: false, code: 'UNKNOWN' };
    return { ok: true, data: body.data };
  }
  return { ok: false, code: mapKycHttpError(response.status) };
}
